import React, { FC, ReactNode, useState } from 'react';
import Text from '../Text/Text';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import Button from '../Button/ButtonV2';
import Image, { ImageProps } from '../Image/Image';
import Spinner from '../Spinner/Spinner';
import { convertFileToBase64 } from '../../utils/image-utils/image-utils';
import { cn } from '../../utils/ui';
import { convertToWebP } from '@creator/ui/utils/image-utils/image-compress';
import { mergeProps } from '../../utils/merge-props/merge-props';

export interface DropzoneProps {
    className?: string;
    rootClassName?: string;
    enableMultiline?: boolean;
    testId?: string;
    maxSize?: number;
    maxUploadSize?: number; // if specified, will compress images above that size to be just under it
    showSelectButton?: boolean;
    showDeleteButton?: boolean;
    showInstructions?: boolean;
    convertImageToBase64?: boolean;
    showRootDropozone?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    onFile: (file: string | File) => void;
    onError?: (error: FileError) => void;
    initialFile?: string;
    instructionsText?: string;
    selectButtonText?: string | ReactNode;
    deleteButtonText?: string;
    type?: 'compact' | 'fullRounded' | 'fullWidth';
    accept?: string | string[];
    height?: string;
    width?: string;
    previewImageProps?: Partial<ImageProps>;
}

const Dropzone: FC<DropzoneProps> = props => {
    const { showRootDropozone = true, isDisabled, rootClassName = '', showDeleteButton, showSelectButton, isLoading, accept = 'image/*', type = 'fullWidth', onError, maxUploadSize = 1000000, maxSize = Infinity, initialFile, onFile, className } = props;

    const [file, setFile] = useState(initialFile);
    const [isCompressing, setIsCompressing] = useState(false);
    const [preview, setPreview] = useState(initialFile);

    const { open, getRootProps, getInputProps, isDragActive } = useDropzone({
        accept,
        noClick: isDisabled || isLoading,
        maxSize: maxSize,
        multiple: false,
        onDrop: acceptedFiles => {
            const file = acceptedFiles[0];
            if (!file) return;

            if (file.type.includes('image'))
                return onImageDrop(file);

            return onFile(file);
        },
        onDropRejected: rejectedFiles => {
            const file = rejectedFiles[0];
            if (!file) return;

            onDropRejected(file);
        }
    });

    async function compressImage(file: File) {
        setIsCompressing(true);
        const compressedFile = await convertToWebP(file);
        setIsCompressing(false);

        return compressedFile.webpBlob;
    }

    async function onImageDrop(file: File): Promise<void> {
        const { convertImageToBase64 = false } = props;
        const compressedFile = await compressImage(file);

        setFile(URL.createObjectURL(compressedFile));
        setPreview(URL.createObjectURL(compressedFile));
        if (!convertImageToBase64) return onFile(compressedFile as File);
        
        const base64 = await convertFileToBase64(compressedFile);
        onFile(base64 as string);
    }

    async function onDropRejected(file: FileRejection): Promise<void> {
        if (!file.errors) return;

        if (onError) onError(file.errors[0]);
        console.warn(file.errors[0]);
    }

    function getClassName(): string {
        const base = 'transition';
        if (type === 'compact') return `${base} flex items-center`;
        return `${base}`;
    }

    function getRootClassName(): string {
        if (!showRootDropozone) return 'hidden';

        let base = 'transition relative flex items-center justify-center cursor-pointer shadow-s1 dark:shadow-s4';

        if (type === 'compact' || type === 'fullRounded') base = `${base} rounded-full`;
        if (type === 'fullWidth') base = `${base} rounded-lg`;

        if (isDragActive) return `${base} text-black dark:text-gray-50 bg-gray-200 dark:bg-gray-700`;

        return `${base} text-gray-500 hover:text-gray-600 dark:text-gray-100 dark:hover:text-gray-200 bg-gray-50 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700`;
    }

    function getPreviewImageClassname(): string {
        const base = 'transition w-full h-full';

        if (type === 'compact' || type === 'fullRounded') return `${base} rounded-full`;
        return `${base} rounded-lg`;
    }

    function getActionButtonsClassName(): string {
        const base = '';

        if (type === 'compact') return `${base} space-y-2 ml-4`;
        return `${base} flex items-center ${showRootDropozone ? 'mt-4' : ''} space-x-3`;
    }

    function onSelectClick(e: React.MouseEvent<HTMLElement>): void {
        e.preventDefault();
        e.stopPropagation();
        open();
    }

    function onDeleteClick(e: React.MouseEvent<HTMLElement>): void {
        e.preventDefault();
        e.stopPropagation();
        setFile('');
        setPreview('');

        onFile('');
    }

    function renderLoading(): ReactNode {
        if (!isCompressing && !isLoading) return null;
        return (
            <div className="absolute inset-0 flex items-center justify-center"><Spinner /></div>
        );
    }

    function renderPreview(): ReactNode {
        const { previewImageProps = {} } = props;
        if (!preview) return null;
        const _props = mergeProps<[Partial<ImageProps>, Partial<ImageProps>]>(
            {
                className: getPreviewImageClassname(),
                isLoading: isCompressing
            },
            previewImageProps
        );
        return <Image {..._props} src={preview} />;
    }

    function renderInstructions(): ReactNode {
        const { instructionsText = '', showInstructions = true } = props;
        if (isCompressing || preview || isLoading) return null;
        return (
            <div className="font-medium text-gray-500 lg:flex lg:flex-col lg:items-center">
                {showInstructions && <Text enableMultiline={true} className="hidden mt-2 text-sm text-center lg:block">{instructionsText}</Text>}
            </div>
        );
    }

    function renderSelectButton(): ReactNode {
        const { selectButtonText = 'Select' } = props;

        if (!showSelectButton) return null;

        return (
            <Button isLoading={isLoading} isDisabled={isDisabled} onClick={onSelectClick} size="sm" color="base" className="">
                {selectButtonText}
            </Button>
        );
    }

    function renderActionButtons(): ReactNode {
        if (!showSelectButton && !showDeleteButton) return null;

        return (
            <div className={getActionButtonsClassName()}>
                {renderSelectButton()}
                {renderDeleteButton()}
            </div>
        );
    }

    function renderDeleteButton(): ReactNode {
        const { deleteButtonText = 'Delete' } = props;

        if (!showDeleteButton) return null;

        return (
            <Button isDisabled={!Boolean(file)} onClick={onDeleteClick} size="sm" color="base" variant="filled" className="">
                <Text>{deleteButtonText}</Text>
            </Button>
        );
    }

    return (
        <div className={`${getClassName()} ${className}`}>
            <div className={cn(getRootClassName(), rootClassName)} {...getRootProps()}>
                <input {...getInputProps()} />
                {renderPreview()}
                {renderInstructions()}
                {renderLoading()}
            </div>
            {renderActionButtons()}
        </div>
    );
};

export default Dropzone;
